import {AuthLoginData, fetchAuthLogin, getPublicAxios} from '@blesk-frontend/api';
import {bleskAuthRedirectStore} from '@blesk-frontend/auth';
import {Button, FieldErrorMessage, FieldGroup, Input, YandexIcon} from '@blesk-frontend/ui';
import {useNavigate, useSearch} from '@tanstack/react-router';
import {SmartCaptcha} from '@yandex/smart-captcha';
import {AxiosError} from 'axios';
import {useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'sonner';
import {useStaffId} from '../hooks';

export const AuthForm = () => {
  const search = useSearch({strict: false}) as {redirect: string};
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const captchaTokenRef = useRef<string | null>(null);

  const [oauthLoading, setOauthLoading] = useState<'yandex' | 'google' | null>(null);
  const [isVisibleCaptcha, setIsVisibleCaptcha] = useState(false);

  const {updateRedirectUrl} = bleskAuthRedirectStore();
  const staffId = useStaffId();

  const {
    formState: {errors, isValid},
    register,
    handleSubmit,
    setError,
    reset,
  } = useForm<AuthLoginData>({
    defaultValues: {
      email: '',
    },
  });

  const handleCaptchaReset = () => {
    captchaTokenRef.current = null;
    setIsVisibleCaptcha(false);
  };

  const handleCaptchaSuccess = (token: string) => {
    captchaTokenRef.current = token;
    formRef.current?.requestSubmit();
  };

  const handleFormPreSubmit = () => {
    if (import.meta.env.DEV) {
      captchaTokenRef.current = '';
      formRef.current?.requestSubmit();
      return;
    }

    setIsVisibleCaptcha(true);
  };

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      updateRedirectUrl(search.redirect || 'null');
      await fetchAuthLogin({email: data.email, staffId, captchaToken: captchaTokenRef.current});
      await navigate({to: '/auth/verify', search: {email: data.email, code: undefined, staffId}});
    } catch (err) {
      if (err instanceof AxiosError && err.status === 501) {
        toast.error('Регистрация пользователей отключена, свяжитесь с администратором.');
        reset({}, {keepDirty: true, keepValues: true});
        setError('root.server', {type: 'server', message: undefined});
        return;
      }
      handleCaptchaReset();
      return err;
    }
  });

  const handleClickOAuth = (provider: 'yandex' | 'google') => {
    setOauthLoading(provider);
    updateRedirectUrl(search.redirect || 'null');
    window.location.href = `${getPublicAxios().defaults.baseURL}/auth/${provider}${staffId ? `?staffId=${staffId}` : ''}`;
  };

  return (
    <form ref={formRef} className="flex flex-col gap-6" onSubmit={handleFormSubmit}>
      <FieldGroup label="Email" labelFor="email">
        <Input
          {...register('email', {
            required: 'Обязательное поле',
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: 'Некорректный email',
            },
          })}
          id="email"
          type="email"
          placeholder="Укажите электронную почту"
          disabled={!!oauthLoading}
        />
        <FieldErrorMessage errors={errors} name="email" />
      </FieldGroup>

      {isVisibleCaptcha ? (
        <div className="flex justify-center">
          <SmartCaptcha
            sitekey={import.meta.env.VITE_SMARTCAPTCHA_CLIENT_KEY as string}
            onSuccess={handleCaptchaSuccess}
            onTokenExpired={handleCaptchaReset}
          />
        </div>
      ) : (
        <Button
          type="button"
          className="w-full"
          disabled={!isValid || !!oauthLoading}
          onClick={handleFormPreSubmit}
        >
          Далее
        </Button>
      )}

      {!import.meta.env.VITE_ON_PREMISE_CLIENT_ID && (
        <>
          <div className="flex items-center">
            <div className="h-px flex-1 bg-border-primary" />
            <span className="px-2 text-paragraph-sm">или</span>
            <div className="h-px flex-1 bg-border-primary" />
          </div>

          <div className="grid">
            <Button
              type="button"
              variant="outline"
              loading={oauthLoading === 'yandex'}
              onClick={() => handleClickOAuth('yandex')}
            >
              <YandexIcon className="size-6" />
              <span>Войти с Яндекс</span>
            </Button>
          </div>
        </>
      )}
    </form>
  );
};
